import React, {ReactElement} from "react";

import Image, {ImageObject} from "../image/Image";
import LinkTag from "../buttons/link-tag/LinkTag";
import ModuleBody from "../layouts/module-body/ModuleBody";

import {css, generateKeyId} from "/src/utils/utils";

import * as styles from "./link-collection.module.scss";

interface LinkCollectionBasicLinkProps {
  icon?: ImageObject;
  label: string;
  openInNewTab?: boolean;
  url: string;
  darkMode?: boolean;
}

const LinkCollectionBasicLink = (
  props: LinkCollectionBasicLinkProps
): ReactElement => {
  const {icon, label, openInNewTab, url, darkMode} = props;

  return (
    <LinkTag
      href={url}
      classes={css(
        styles.linkCollectionBasicLink,
        darkMode ? styles.linkCollectionBasicLinkDark : ""
      )}
      openInNewTab={openInNewTab === true}
    >
      {icon && (
        <Image image={icon} classes={styles.linkCollectionBasicLink__icon} />
      )}

      <p className={css("h5", styles.linkCollectionBasicLink__lbl)}>{label}</p>
    </LinkTag>
  );
};

interface LinkCollectionLinkColumnProps {
  title?: string;
  darkMode?: boolean;
  links: Array<NonNullable<Queries.BasicLinkFragmentFragment>>;
  isSingleLayout?: boolean;
}

const LinkCollectionLinkColumn = (
  props: LinkCollectionLinkColumnProps
): ReactElement => {
  const {title, links, darkMode, isSingleLayout} = props;

  return (
    <div
      className={css(
        styles.linkCollectionLinkColumn,
        darkMode ? styles.linkCollectionLinkColumnDark : "",
        isSingleLayout ? styles.linkCollectionLinkColumnSingle : ""
      )}
    >
      {title && (
        <h3 className={css("h4", styles.linkCollectionLinkColumn__title)}>
          {title}
        </h3>
      )}

      {links && links.length > 0 && (
        <ul className={styles.linkCollectionLinkColumn__list}>
          {links.map((link, i) => {
            const key = generateKeyId(`link-collection-column-basic-link-${i}`);

            return (
              <li key={key} className={styles.linkCollectionLinkColumn__item}>
                <LinkCollectionBasicLink
                  icon={link.icon as ImageObject}
                  label={link.label!}
                  openInNewTab={link.openInNewTab!}
                  darkMode={darkMode === true}
                  url={link.url!}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

interface LinkCollectionProps extends Queries.LinkCollectionFragmentFragment {
  singleColumnLayout?: boolean;
}

const LinkCollection = (props: LinkCollectionProps): ReactElement | null => {
  const {
    headline,
    theme,
    column1Title,
    column1Links,
    column2Title,
    column2Links,
    column3Title,
    column3Links,
    column4Title,
    column4Links,
    singleColumnLayout,
  } = props;

  const isDarkMode = (theme! || "light").toLowerCase() === "dark";

  let contentToRender: ReactElement | null = null;

  if (singleColumnLayout) {
    const allLinkGroups = [
      column1Links,
      column2Links,
      column3Links,
      column4Links,
    ];
    const combinedLinks: Array<NonNullable<Queries.BasicLinkFragmentFragment>> =
      [];

    allLinkGroups.forEach(group => {
      if (group) {
        group.forEach(link => {
          if (link) {
            combinedLinks.push(
              link as NonNullable<Queries.BasicLinkFragmentFragment>
            );
          }
        });
      }
    });

    if (combinedLinks.length > 0) {
      contentToRender = (
        <LinkCollectionLinkColumn
          links={combinedLinks}
          darkMode={isDarkMode}
          isSingleLayout={true}
        />
      );
    }
  } else {
    const rawColumnsData = [
      {title: column1Title, links: column1Links},
      {title: column2Title, links: column2Links},
      {title: column3Title, links: column3Links},
      {title: column4Title, links: column4Links},
    ];

    const validColumns = rawColumnsData
      .map(col => ({
        title: col.title || undefined,
        links:
          (col.links?.filter(link => link !== null) as Array<
            NonNullable<Queries.BasicLinkFragmentFragment>
          >) || [],
      }))
      .filter(col => col.links.length > 0);

    if (validColumns.length > 0) {
      contentToRender = (
        <ul className={styles.linkCollection__list}>
          {validColumns.map((column, i) => {
            const key = generateKeyId(`link-collection-link-column-${i}`);
            return (
              <li key={key} className={styles.linkCollection__item}>
                <LinkCollectionLinkColumn
                  title={column.title}
                  links={column.links}
                  darkMode={isDarkMode}
                  isSingleLayout={false}
                />
              </li>
            );
          })}
        </ul>
      );
    }
  }

  const headlineElement = headline && (
    <h2
      className={css(
        singleColumnLayout ? "h3" : "h1",
        styles.linkCollection__headline
      )}
    >
      {headline}
    </h2>
  );

  return (
    <section
      className={css(
        styles.linkCollection,
        isDarkMode ? styles.linkCollectionDark : "",
        singleColumnLayout ? styles.linkCollectionSingle : ""
      )}
    >
      {singleColumnLayout ? (
        // For single column layout, render headline and content directly
        <>
          {headlineElement}
          {contentToRender}
        </>
      ) : (
        // For multi-column layout, wrap headline and content in ModuleBody
        <ModuleBody>
          {headlineElement}
          {contentToRender}
        </ModuleBody>
      )}
    </section>
  );
};

export default LinkCollection;
